import React, { useState, useEffect } from "react";
import { auth, db } from "../firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

export default function Profile({ user }) {
  const [profile, setProfile] = useState({ fullName: "", phone: "", country: "", dob: "" });
  const [kyc, setKyc] = useState({ idDoc: null, proofOfResidence: null, bankStatement: null });
  const [kycStatus, setKycStatus] = useState({ idDoc: null, proofOfResidence: null, bankStatement: null });
  const [uploading, setUploading] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    const fetchProfile = async () => {
      const ref = doc(db, "users", user.uid);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        setProfile({
          fullName: data.fullName || "",
          phone: data.phone || "",
          country: data.country || "",
          dob: data.dob || "",
        });
        setKycStatus({
          idDoc: data.kyc?.idDoc || null,
          proofOfResidence: data.kyc?.proofOfResidence || null,
          bankStatement: data.kyc?.bankStatement || null,
        });
      }
    };
    fetchProfile();
  }, [user]);

  const saveProfile = async () => {
    setSaving(true); setMsg("");
    try {
      await setDoc(doc(db, "users", user.uid), {
        ...profile,
        email: user.email,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setMsg("✓ Profile saved successfully!");
    } catch (e) {
      setMsg("Error: " + e.message);
    }
    setSaving(false);
  };

  const uploadDoc = async (type, file) => {
    if (!file) return;
    setUploading(prev => ({ ...prev, [type]: true }));
    setMsg("");
    try {
      const storage = getStorage();
      const storageRef = ref(storage, `kyc/${user.uid}/${type}_${Date.now()}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      await setDoc(doc(db, "users", user.uid), {
        kyc: { [type]: { url, status: "pending", uploadedAt: new Date().toISOString() } },
        email: user.email,
      }, { merge: true });
      setKycStatus(prev => ({ ...prev, [type]: { url, status: "pending" } }));
      setMsg("✓ Document uploaded! Pending review by admin.");
    } catch (e) {
      setMsg("Error: " + e.message);
    }
    setUploading(prev => ({ ...prev, [type]: false }));
  };

  const getStatusBadge = (status) => {
    if (!status) return <span className="kyc-badge kyc-none">Not uploaded</span>;
    if (status.status === "pending") return <span className="kyc-badge kyc-pending">⏳ Pending Review</span>;
    if (status.status === "approved") return <span className="kyc-badge kyc-approved">✓ Approved</span>;
    if (status.status === "rejected") return <span className="kyc-badge kyc-rejected">✗ Rejected</span>;
    return null;
  };

  return (
    <div className="profile-page">
      <div className="profile-title">My Profile & KYC Verification</div>
      <div className="profile-subtitle">Complete your profile and upload required documents to unlock full trading features.</div>

      <div className="profile-grid">
        <div className="profile-card">
          <div className="profile-card-title">Personal Information</div>
          <label className="dep-label">Full Name</label>
          <input className="inp" placeholder="As per your ID document" value={profile.fullName} onChange={e => setProfile({...profile, fullName: e.target.value})} />
          <label className="dep-label">Email</label>
          <input className="inp" value={user.email} disabled style={{opacity:0.5}} />
          <label className="dep-label">Phone Number</label>
          <input className="inp" placeholder="+1 234 567 8900" value={profile.phone} onChange={e => setProfile({...profile, phone: e.target.value})} />
          <label className="dep-label">Country of Residence</label>
          <input className="inp" placeholder="e.g. Indonesia, Turkey" value={profile.country} onChange={e => setProfile({...profile, country: e.target.value})} />
          <label className="dep-label">Date of Birth</label>
          <input className="inp" type="date" value={profile.dob} onChange={e => setProfile({...profile, dob: e.target.value})} />
          {msg && (
            <div className="dep-msg" style={{
              color: msg.includes("✓") ? "#00C896" : "#FF3B5C",
              background: msg.includes("✓") ? "rgba(0,200,150,0.08)" : "rgba(255,59,92,0.08)"
            }}>{msg}</div>
          )}
          <button className="btn-main" onClick={saveProfile} disabled={saving}>
            {saving ? "Saving..." : "Save Profile"}
          </button>
        </div>

        <div className="profile-card">
          <div className="profile-card-title">KYC Verification</div>
          <div className="profile-kyc-info">
            Upload the required documents below. All documents must be clear, valid, and not expired.
          </div>

          <div className="kyc-doc-card">
            <div className="kyc-doc-header">
              <div>
                <div className="kyc-doc-title">📋 Proof of Identity</div>
                <div className="kyc-doc-sub">National ID, Passport, or Driver's License</div>
              </div>
              {getStatusBadge(kycStatus.idDoc)}
            </div>
            <div className="kyc-doc-reqs">
              <span>✓ Clear photo of front & back</span>
              <span>✓ Must not be expired</span>
              <span>✓ Full name must be visible</span>
            </div>
            <div className="dep-upload" onClick={() => document.getElementById("upload-id").click()}>
              <input id="upload-id" type="file" accept="image/*,.pdf" style={{display:"none"}}
                onChange={e => { setKyc({...kyc, idDoc: e.target.files[0]}); uploadDoc("idDoc", e.target.files[0]); }} />
              {uploading.idDoc ? "Uploading..." : kycStatus.idDoc ? "Re-upload document" : "Click to upload"}
            </div>
          </div>

          <div className="kyc-doc-card">
            <div className="kyc-doc-header">
              <div>
                <div className="kyc-doc-title">🏠 Proof of Residence</div>
                <div className="kyc-doc-sub">Utility bill, bank statement, or government letter</div>
              </div>
              {getStatusBadge(kycStatus.proofOfResidence)}
            </div>
            <div className="kyc-doc-reqs">
              <span>✓ Electricity, water, or gas bill</span>
              <span>✓ Not older than 3 months</span>
              <span>✓ Full address must be visible</span>
            </div>
            <div className="dep-upload" onClick={() => document.getElementById("upload-por").click()}>
              <input id="upload-por" type="file" accept="image/*,.pdf" style={{display:"none"}}
                onChange={e => { setKyc({...kyc, proofOfResidence: e.target.files[0]}); uploadDoc("proofOfResidence", e.target.files[0]); }} />
              {uploading.proofOfResidence ? "Uploading..." : kycStatus.proofOfResidence ? "Re-upload document" : "Click to upload"}
            </div>
          </div>

          <div className="kyc-doc-card">
            <div className="kyc-doc-header">
              <div>
                <div className="kyc-doc-title">🏦 Bank Statement</div>
                <div className="kyc-doc-sub">Required for international wire transfers</div>
              </div>
              {getStatusBadge(kycStatus.bankStatement)}
            </div>
            <div className="kyc-doc-reqs">
              <span>✓ Must include SWIFT/BIC code</span>
              <span>✓ Must include IBAN number</span>
              <span>✓ Bank name & account holder visible</span>
              <span>✓ Not older than 3 months</span>
            </div>
            <div className="dep-upload" onClick={() => document.getElementById("upload-bank").click()}>
              <input id="upload-bank" type="file" accept="image/*,.pdf" style={{display:"none"}}
                onChange={e => { setKyc({...kyc, bankStatement: e.target.files[0]}); uploadDoc("bankStatement", e.target.files[0]); }} />
              {uploading.bankStatement ? "Uploading..." : kycStatus.bankStatement ? "Re-upload document" : "Click to upload"}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}